import { useEffect, useMemo, useState } from "react";
import {
  Anchor,
  Badge,
  Button,
  Container,
  Group,
  Modal,
  MultiSelect,
  NumberInput,
  Select,
  Stack,
  Table,
  Tabs,
  Text,
  TextInput,
  Textarea,
  Title,
  UnstyledButton,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconArrowDown,
  IconArrowUp,
  IconArrowsSort,
  IconBuildingStore,
  IconPlus,
  IconSearch,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import { ApiError, api } from "../api/client";
import type { PurchaseOrder, PurchaseOrderStatus, Supplier } from "../api/types";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { SuppliersModal } from "../components/procurement/SuppliersModal";
import { ExpensesTab } from "../components/procurement/ExpensesTab";
import { ResponsiveTable } from "../components/responsiveTable/ResponsiveTable";

const ALL_STATUSES: PurchaseOrderStatus[] = ["ordered", "in_transit", "at_customs", "received"];

type SortKey = "code" | "supplier" | "title" | "supplier_order_id" | "ordered_at" | "total_amount" | "status";
type SortDir = "asc" | "desc";

function statusColor(s: PurchaseOrderStatus): string {
  return ({ ordered: "gray", in_transit: "blue", at_customs: "orange", received: "teal" } as const)[s];
}

export function Procurement() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useCurrentUser();
  const isAdmin = user.role === "admin";

  const [orders, setOrders] = useState<PurchaseOrder[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [statusFilter, setStatusFilter] = useState<PurchaseOrderStatus[]>([]);
  const [supplierFilter, setSupplierFilter] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("ordered_at");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [loading, setLoading] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [suppliersModalOpen, setSuppliersModalOpen] = useState(false);
  const [form, setForm] = useState({
    supplier_id: null as string | null,
    title: "",
    supplier_order_id: "",
    ordered_at: new Date().toISOString().slice(0, 10),
    total_amount: "",
    total_currency: "USD" as "USD" | "EUR" | "MDL",
    notes: "",
  });

  async function reload() {
    setLoading(true);
    try {
      const [ords, sups] = await Promise.all([
        api.listOrders({
          status: statusFilter.length > 0 ? statusFilter : undefined,
          supplier_id: supplierFilter ? Number(supplierFilter) : undefined,
        }),
        api.listSuppliers(true),
      ]);
      setOrders(ords);
      setSuppliers(sups);
    } catch (e) {
      notifications.show({ color: "red", message: e instanceof ApiError ? e.message : String(e) });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void reload(); }, [statusFilter.join(","), supplierFilter]);

  const supplierOptions = suppliers.map((s) => ({ value: String(s.id), label: s.name }));
  const supplierById = new Map(suppliers.map((s) => [s.id, s.name] as const));

  function toggleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir(key === "ordered_at" || key === "total_amount" ? "desc" : "asc");
    }
  }

  const displayedOrders = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = q
      ? orders.filter((o) => {
          const supName = (supplierById.get(o.supplier_id) ?? "").toLowerCase();
          const haystack = [
            o.code,
            o.title ?? "",
            o.supplier_order_id ?? "",
            supName,
            o.ordered_at,
            o.received_at ?? "",
            o.notes ?? "",
            o.total_amount != null ? String(o.total_amount) : "",
            o.total_currency ?? "",
            o.status,
          ]
            .join(" ")
            .toLowerCase();
          return haystack.includes(q);
        })
      : orders;

    const dir = sortDir === "asc" ? 1 : -1;
    const sortable = [...filtered];
    sortable.sort((a, b) => {
      let av: string | number = "";
      let bv: string | number = "";
      switch (sortKey) {
        case "code":
          av = a.code; bv = b.code; break;
        case "supplier":
          av = supplierById.get(a.supplier_id) ?? "";
          bv = supplierById.get(b.supplier_id) ?? ""; break;
        case "title":
          av = a.title ?? ""; bv = b.title ?? ""; break;
        case "supplier_order_id":
          av = a.supplier_order_id ?? ""; bv = b.supplier_order_id ?? ""; break;
        case "ordered_at":
          av = a.ordered_at; bv = b.ordered_at; break;
        case "total_amount":
          av = a.total_amount ?? -Infinity;
          bv = b.total_amount ?? -Infinity; break;
        case "status":
          av = a.status; bv = b.status; break;
      }
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
    return sortable;
  }, [orders, search, sortKey, sortDir, supplierById]);

  function SortHeader({ k, label }: { k: SortKey; label: string }) {
    const isActive = sortKey === k;
    const Icon = isActive ? (sortDir === "asc" ? IconArrowUp : IconArrowDown) : IconArrowsSort;
    return (
      <UnstyledButton
        onClick={() => toggleSort(k)}
        style={{ display: "inline-flex", alignItems: "center", gap: 4, cursor: "pointer", fontWeight: 600 }}
      >
        {label}
        <Icon size={12} style={{ opacity: isActive ? 1 : 0.4 }} />
      </UnstyledButton>
    );
  }

  async function submitCreate() {
    if (!form.supplier_id || !form.ordered_at) {
      notifications.show({ color: "red", message: t("procurement.form.required") });
      return;
    }
    try {
      const newOrder = await api.createOrder({
        supplier_id: Number(form.supplier_id),
        ordered_at: form.ordered_at,
        title: form.title.trim() || null,
        supplier_order_id: form.supplier_order_id.trim() || null,
        total_amount: form.total_amount ? Number(form.total_amount) : null,
        total_currency: form.total_currency,
        notes: form.notes.trim() || null,
      });
      setCreateOpen(false);
      setForm({ ...form, title: "", supplier_order_id: "", total_amount: "", notes: "" });
      navigate(`/procurement/${newOrder.id}`);
    } catch (e) {
      notifications.show({ color: "red", message: e instanceof ApiError ? e.message : String(e) });
    }
  }

  return (
    <Container size="xl">
      <Title order={2} mb="md">{t("procurement.title")}</Title>
      <Tabs defaultValue="orders">
        <Tabs.List mb="md">
          <Tabs.Tab value="orders">{t("procurement.tabOrders")}</Tabs.Tab>
          <Tabs.Tab value="expenses">{t("procurement.tabExpenses")}</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="orders">
      <Group mb="md" gap="xs" wrap="wrap">
          {isAdmin && (
            <Button leftSection={<IconPlus size={16} />} onClick={() => setCreateOpen(true)}>
              {t("procurement.addOrder")}
            </Button>
          )}
          {isAdmin && (
            <Button variant="default" leftSection={<IconBuildingStore size={16} />} onClick={() => setSuppliersModalOpen(true)}>
              {t("procurement.manageSuppliers")}
            </Button>
          )}
      </Group>

      <Group gap="sm" mb="md" wrap="wrap" align="end">
        <TextInput
          label={t("procurement.searchLabel")}
          placeholder={t("procurement.searchPlaceholder")}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          leftSection={<IconSearch size={14} />}
          w={280}
        />
        <MultiSelect
          label={t("procurement.statusFilter")}
          data={ALL_STATUSES.map((s) => ({ value: s, label: t(`procurement.status.${s}`) }))}
          value={statusFilter}
          onChange={(v) => setStatusFilter(v as PurchaseOrderStatus[])}
          clearable
          w={320}
          maw="100%"
        />
        <Select
          label={t("procurement.supplierFilter")}
          data={supplierOptions}
          value={supplierFilter}
          onChange={setSupplierFilter}
          clearable
          searchable
          w={220}
        />
      </Group>

      <ResponsiveTable
        mobile={{
          rows: displayedOrders,
          sort: {
            options: [
              { value: "code", label: t("procurement.col.code") },
              { value: "supplier", label: t("procurement.col.supplier") },
              { value: "supplier_order_id", label: t("procurement.col.externalId") },
              { value: "ordered_at", label: t("procurement.col.orderedAt") },
              ...(isAdmin ? [{ value: "total_amount", label: t("procurement.col.total") }] : []),
              { value: "status", label: t("procurement.col.status") },
            ],
            value: sortKey,
            dir: sortDir,
            onChange: (v, d) => (v === sortKey ? setSortDir(d) : toggleSort(v as SortKey)),
          },
          rowKey: (o) => o.id,
          title: (o) => o.code,
          subtitle: (o) => (o.title ? `${o.ordered_at} · ${o.title}` : o.ordered_at),
          badge: (o) => (
            <Badge color={statusColor(o.status)} variant="light">
              {t(`procurement.status.${o.status}`)}
            </Badge>
          ),
          fields: [
            { label: t("procurement.col.supplier"), render: (o) => supplierById.get(o.supplier_id) ?? `#${o.supplier_id}` },
            { label: t("procurement.col.externalId"), render: (o) => o.supplier_order_id ?? "—" },
            ...(isAdmin
              ? [{
                  label: t("procurement.col.total"),
                  render: (o: PurchaseOrder) => (o.total_amount != null ? `${o.total_amount.toFixed(2)} ${o.total_currency ?? ""}` : "—"),
                }]
              : []),
          ],
          onOpen: (o) => navigate(`/procurement/${o.id}`),
          loading: loading && displayedOrders.length === 0,
          empty: search ? t("procurement.noMatch") : t("procurement.empty"),
        }}
      >
      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th><SortHeader k="code" label={t("procurement.col.code")} /></Table.Th>
            <Table.Th><SortHeader k="supplier" label={t("procurement.col.supplier")} /></Table.Th>
            <Table.Th><SortHeader k="supplier_order_id" label={t("procurement.col.externalId")} /></Table.Th>
            <Table.Th><SortHeader k="ordered_at" label={t("procurement.col.orderedAt")} /></Table.Th>
            {isAdmin && <Table.Th><SortHeader k="total_amount" label={t("procurement.col.total")} /></Table.Th>}
            <Table.Th><SortHeader k="status" label={t("procurement.col.status")} /></Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {displayedOrders.length === 0 ? (
            <Table.Tr>
              <Table.Td colSpan={isAdmin ? 6 : 5}>
                <Text c="dimmed" size="sm">
                  {loading ? t("common.loading") : (search ? t("procurement.noMatch") : t("procurement.empty"))}
                </Text>
              </Table.Td>
            </Table.Tr>
          ) : (
            displayedOrders.map((o) => (
              <Table.Tr key={o.id}>
                <Table.Td>
                  <Anchor component={Link} to={`/procurement/${o.id}`}>{o.code}</Anchor>
                  {o.title && (
                    <Text size="xs" c="dimmed" mt={2}>{o.title}</Text>
                  )}
                </Table.Td>
                <Table.Td>{supplierById.get(o.supplier_id) ?? `#${o.supplier_id}`}</Table.Td>
                <Table.Td>{o.supplier_order_id ?? "—"}</Table.Td>
                <Table.Td>{o.ordered_at}</Table.Td>
                {isAdmin && (
                  <Table.Td>
                    {o.total_amount != null ? `${o.total_amount.toFixed(2)} ${o.total_currency ?? ""}` : "—"}
                  </Table.Td>
                )}
                <Table.Td>
                  <Badge color={statusColor(o.status)} variant="light">
                    {t(`procurement.status.${o.status}`)}
                  </Badge>
                </Table.Td>
              </Table.Tr>
            ))
          )}
        </Table.Tbody>
      </Table>
      </ResponsiveTable>

      <Modal opened={createOpen} onClose={() => setCreateOpen(false)} title={t("procurement.addOrder")} centered>
        <Stack>
          <Select
            label={t("procurement.col.supplier")}
            data={supplierOptions}
            value={form.supplier_id}
            onChange={(v) => setForm({ ...form, supplier_id: v })}
            searchable
            required
          />
          <TextInput
            label={t("procurement.col.title")}
            placeholder={t("procurement.titlePlaceholder")}
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.currentTarget.value })}
          />
          <TextInput
            type="date"
            label={t("procurement.col.orderedAt")}
            value={form.ordered_at}
            onChange={(e) => setForm({ ...form, ordered_at: e.currentTarget.value })}
            required
          />
          <TextInput
            label={t("procurement.col.externalId")}
            value={form.supplier_order_id}
            onChange={(e) => setForm({ ...form, supplier_order_id: e.currentTarget.value })}
          />
          <Group grow>
            <NumberInput
              label={t("procurement.col.total")}
              value={form.total_amount}
              onChange={(v) => setForm({ ...form, total_amount: typeof v === "number" ? String(v) : (v ?? "") })}
              min={0}
              decimalScale={2}
              allowNegative={false}
            />
            <Select
              label={t("procurement.col.currency")}
              data={["USD", "EUR", "MDL"]}
              value={form.total_currency}
              onChange={(v) => setForm({ ...form, total_currency: (v as "USD" | "EUR" | "MDL") ?? "USD" })}
              allowDeselect={false}
            />
          </Group>
          <Textarea
            label={t("procurement.col.notes")}
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.currentTarget.value })}
            minRows={2}
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setCreateOpen(false)}>{t("common.cancel")}</Button>
            <Button onClick={() => void submitCreate()}>{t("common.save")}</Button>
          </Group>
        </Stack>
      </Modal>

      <SuppliersModal opened={suppliersModalOpen} onClose={() => setSuppliersModalOpen(false)} onChange={reload} />
        </Tabs.Panel>
        <Tabs.Panel value="expenses">
          <ExpensesTab />
        </Tabs.Panel>
      </Tabs>
    </Container>
  );
}
